import { useEffect, useMemo, useState } from "react";
import {
  AlertTriangle,
  ArrowLeft,
  CheckCircle2,
  ChevronRight,
  FileText,
  Image as ImageIcon,
  Loader2,
  MapPin,
  MessageSquareText,
  Phone,
  RotateCcw,
  UserRound,
} from "lucide-react";
import { apiFetch, type CurrentUser } from "./lib/api";
import "./customer-review-workbench.css";

type CustomerDetail = {
  id: string;
  name: string;
  companyName?: string | null;
  contactName?: string | null;
  contactTitle?: string | null;
  mobile?: string | null;
  wechat?: string | null;
  salesLane?: string | null;
  businessCategory?: string | null;
  commerceChannel?: string | null;
  customerType?: string | null;
  province?: string | null;
  city?: string | null;
  cooperationContent?: string | null;
  expectedScale?: string | null;
  firstSubstantiveContactAt?: string | null;
  createdAt?: string | null;
  owner?: { id: string; displayName: string } | null;
  followups?: Array<{
    id: string;
    content?: string | null;
    keyPoints?: string | null;
    nextAction?: string | null;
    nextFollowupAt?: string | null;
    followupDate?: string | null;
  }>;
};

type EvidenceFile = {
  id: string;
  fileName: string;
  fileUrl: string;
  fileType?: string | null;
  category?: string | null;
  note?: string | null;
  createdAt: string;
};

type EvidenceResponse = { files: EvidenceFile[] };

type IntakeReview = {
  status: "PENDING" | "APPROVED" | "RETURNED";
  remark?: string | null;
  reviewedAt?: string | null;
  reviewer?: { id: string; displayName: string } | null;
};

const previewCustomer: CustomerDetail = {
  id: "preview-customer-1",
  name: "北京丰禾商贸",
  companyName: "北京丰禾商贸有限公司",
  contactName: "周明远",
  contactTitle: "渠道采购经理",
  mobile: "138****6802",
  wechat: "zhou_mingyuan",
  salesLane: "ECOTECH",
  businessCategory: "ECOMMERCE",
  commerceChannel: "OFFLINE",
  customerType: "代理商",
  province: "北京",
  city: "朝阳区",
  cooperationContent: "希望引入环保日化产品，先做3家门店试销。",
  expectedScale: "首批约5万元",
  firstSubstantiveContactAt: new Date().toISOString(),
  createdAt: new Date().toISOString(),
  owner: { id: "preview-sales", displayName: "孙小松" },
  followups: [
    {
      id: "preview-followup",
      content: "已当面介绍洗护产品，客户关心供货周期与首批试销支持。",
      keyPoints: "关注供货周期、物料支持和试销政策。",
      nextAction: "发送产品报价与试销方案",
      nextFollowupAt: new Date(Date.now() + 86400000).toISOString(),
      followupDate: new Date().toISOString(),
    },
  ],
};

function formatDate(value?: string | null) {
  if (!value) return "未填写";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "未填写";
  return new Intl.DateTimeFormat("zh-CN", {
    month: "numeric",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).format(date);
}

function evidenceHref(fileUrl: string) {
  return new URL(fileUrl, window.location.origin).toString();
}

function isCommunicationEvidence(file: EvidenceFile) {
  return ["COMMUNICATION_EVIDENCE", "COMMUNICATION_SCREENSHOT"].includes(
    file.category ?? "",
  );
}

export function CustomerReviewWorkbench({
  customerId,
  user,
}: {
  customerId: string;
  user: CurrentUser;
}) {
  const previewMode = import.meta.env.DEV && user.id.startsWith("preview-");
  const [customer, setCustomer] = useState<CustomerDetail | null>(null);
  const [files, setFiles] = useState<EvidenceFile[]>([]);
  const [review, setReview] = useState<IntakeReview | null>(null);
  const [remark, setRemark] = useState("");
  const [loading, setLoading] = useState(true);
  const [reviewing, setReviewing] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");

  async function loadReview() {
    setLoading(true);
    setError("");
    try {
      if (previewMode) {
        setCustomer(previewCustomer);
        setFiles([]);
        setReview({ status: "PENDING" });
        return;
      }
      if (!customerId) throw new Error("通知中缺少客户编号");
      const [nextCustomer, evidence, nextReview] = await Promise.all([
        apiFetch<CustomerDetail>(
          `/customers/${encodeURIComponent(customerId)}`,
        ),
        apiFetch<EvidenceResponse>(
          `/customers/${encodeURIComponent(customerId)}/evidence`,
        ),
        apiFetch<IntakeReview>(
          `/customers/${encodeURIComponent(customerId)}/intake-review`,
        ),
      ]);
      setCustomer(nextCustomer);
      setFiles(evidence.files);
      setReview(nextReview);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "客户资料读取失败");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void loadReview();
  }, [customerId, previewMode]);

  const reviewSummary = useMemo(() => {
    if (!customer) return null;
    const latestFollowup = customer.followups?.[0];
    const missing = [
      !customer.contactName ? "联系人" : null,
      !customer.contactTitle ? "联系人职务" : null,
      !(customer.mobile || customer.wechat) ? "联系方式" : null,
      !customer.salesLane ? "经营线" : null,
      !customer.customerType ? "客户类型" : null,
      !(customer.province || customer.city) ? "所在地区" : null,
      !customer.cooperationContent ? "客户需求" : null,
      !customer.expectedScale ? "预计规模" : null,
      !customer.firstSubstantiveContactAt ? "实质沟通时间" : null,
      !latestFollowup ? "实质沟通记录" : null,
      latestFollowup && !latestFollowup.nextAction ? "下一步动作" : null,
      latestFollowup && !latestFollowup.nextFollowupAt ? "下一步日期" : null,
      !files.some(isCommunicationEvidence) ? "沟通截图" : null,
    ].filter((item): item is string => Boolean(item));
    return {
      latestFollowup,
      missing,
      completeness: Math.round(((13 - missing.length) / 13) * 100),
    };
  }, [customer, files]);

  async function submitReview(decision: "APPROVED" | "RETURNED") {
    if (decision === "RETURNED" && remark.trim().length < 4) {
      setError("退回时请填写至少4个字的补充说明");
      return;
    }
    setReviewing(true);
    setError("");
    setMessage("");
    try {
      if (previewMode) {
        await new Promise((resolve) => window.setTimeout(resolve, 350));
        setReview({
          status: decision === "APPROVED" ? "APPROVED" : "RETURNED",
          remark: remark.trim() || null,
          reviewedAt: new Date().toISOString(),
          reviewer: { id: user.id, displayName: user.displayName },
        });
      } else {
        const nextReview = await apiFetch<IntakeReview>(
          `/customers/${encodeURIComponent(customerId)}/intake-review`,
          {
            method: "POST",
            body: JSON.stringify({ decision, remark: remark.trim() }),
          },
        );
        setReview(nextReview);
      }
      setRemark("");
      setMessage(
        decision === "APPROVED"
          ? `已确认资料准确，并通知${customer?.owner?.displayName ?? "负责人"}。`
          : `已退回补充，并通知${customer?.owner?.displayName ?? "负责人"}。`,
      );
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "核对结果提交失败");
    } finally {
      setReviewing(false);
    }
  }

  function goBack() {
    if (window.history.length > 1) window.history.back();
    else window.location.href = "/mobile";
  }

  if (loading) {
    return (
      <main className="customer-review-page customer-review-centered">
        <Loader2 className="spin" size={25} />
        <strong>正在打开客户资料…</strong>
        <span>即将显示孙小松录入的内容和沟通截图</span>
      </main>
    );
  }

  if (!customer || !reviewSummary) {
    return (
      <main className="customer-review-page customer-review-centered">
        <AlertTriangle size={28} />
        <strong>无法打开这位客户</strong>
        <span>{error || "请返回通知后重试"}</span>
        <button onClick={() => void loadReview()} type="button">
          重新读取
        </button>
      </main>
    );
  }

  const communicationFiles = files.filter(isCommunicationEvidence);
  const latestFollowup = reviewSummary.latestFollowup;

  return (
    <main className="customer-review-page">
      <header className="customer-review-topbar">
        <button aria-label="返回" onClick={goBack} type="button">
          <ArrowLeft size={21} />
        </button>
        <div>
          <span>客户录入核对</span>
          <strong>{customer.owner?.displayName ?? "负责人"}提交</strong>
        </div>
        <span className="customer-review-avatar">
          {user.displayName.slice(0, 1)}
        </span>
      </header>

      <section className="customer-review-hero">
        <div>
          <span>客户资料</span>
          <h1>{customer.name}</h1>
          <p>{customer.companyName || "未填写企业全称"}</p>
        </div>
        <div
          className={`customer-review-score ${reviewSummary.completeness === 100 ? "complete" : ""}`}
        >
          <strong>{reviewSummary.completeness}%</strong>
          <span>完整度</span>
        </div>
      </section>

      <section className="customer-review-status">
        {review?.status === "APPROVED" ? (
          <CheckCircle2 size={20} />
        ) : review?.status === "RETURNED" ? (
          <RotateCcw size={20} />
        ) : (
          <MessageSquareText size={20} />
        )}
        <div>
          <strong>
            {review?.status === "APPROVED"
              ? "已确认准确"
              : review?.status === "RETURNED"
                ? "已退回补充"
                : "等待您核对"}
          </strong>
          <span>
            {review?.status === "RETURNED"
              ? review.remark || "请完善资料"
              : review?.reviewedAt
                ? `${review.reviewer?.displayName ?? "管理者"} · ${formatDate(review.reviewedAt)}`
                : "资料与沟通截图都在本页"}
          </span>
        </div>
      </section>

      {reviewSummary.missing.length ? (
        <section className="customer-review-missing">
          <strong>待完善 {reviewSummary.missing.length} 项</strong>
          <div>
            {reviewSummary.missing.map((item) => (
              <span key={item}>{item}</span>
            ))}
          </div>
        </section>
      ) : (
        <section className="customer-review-complete">
          <CheckCircle2 size={19} />
          13项核对内容已完善
        </section>
      )}

      <section className="customer-review-card">
        <header>
          <UserRound size={19} />
          <div>
            <span>01</span>
            <strong>客户与联系人</strong>
          </div>
        </header>
        <dl className="customer-review-grid">
          <div>
            <dt>联系人</dt>
            <dd>{customer.contactName || "未填写"}</dd>
          </div>
          <div>
            <dt>职务</dt>
            <dd>{customer.contactTitle || "未填写"}</dd>
          </div>
          <div>
            <dt>
              <Phone size={14} />
              手机
            </dt>
            <dd>{customer.mobile || "未填写"}</dd>
          </div>
          <div>
            <dt>微信</dt>
            <dd>{customer.wechat || "未填写"}</dd>
          </div>
          <div className="wide">
            <dt>
              <MapPin size={14} />
              地区
            </dt>
            <dd>
              {[customer.province, customer.city].filter(Boolean).join(" ") ||
                "未填写"}
            </dd>
          </div>
          <div>
            <dt>渠道类型</dt>
            <dd>{customer.customerType || "未填写"}</dd>
          </div>
          <div>
            <dt>线上 / 线下</dt>
            <dd>
              {customer.commerceChannel === "OFFLINE"
                ? "线下"
                : customer.commerceChannel === "ONLINE"
                  ? "线上"
                  : "未填写"}
            </dd>
          </div>
        </dl>
      </section>

      <section className="customer-review-card">
        <header>
          <MessageSquareText size={19} />
          <div>
            <span>02</span>
            <strong>本次实质沟通</strong>
          </div>
        </header>
        <div className="customer-review-copy">
          <label>沟通时间</label>
          <p>{formatDate(customer.firstSubstantiveContactAt)}</p>
          <label>客户需求</label>
          <p>{customer.cooperationContent || "未填写"}</p>
          <label>预计规模</label>
          <p>{customer.expectedScale || "未填写"}</p>
          <label>沟通结论</label>
          <p>{latestFollowup?.content || "未填写"}</p>
          <label>下一步</label>
          <p>{latestFollowup?.nextAction || "未填写"}</p>
          <label>下次时间</label>
          <p>{formatDate(latestFollowup?.nextFollowupAt)}</p>
        </div>
      </section>

      <section className="customer-review-card">
        <header>
          <ImageIcon size={19} />
          <div>
            <span>03</span>
            <strong>沟通截图</strong>
          </div>
          <em>{communicationFiles.length}张</em>
        </header>
        {communicationFiles.length ? (
          <div className="customer-review-evidence">
            {communicationFiles.map((file) => {
              const isImage = file.fileType?.startsWith("image/");
              return (
                <a
                  href={evidenceHref(file.fileUrl)}
                  key={file.id}
                  rel="noreferrer"
                  target="_blank"
                >
                  {isImage ? (
                    <img alt={file.fileName} src={evidenceHref(file.fileUrl)} />
                  ) : (
                    <FileText size={30} />
                  )}
                  <span>
                    <strong>{file.fileName}</strong>
                    <small>{file.note || "点击查看原图"}</small>
                  </span>
                  <ChevronRight size={18} />
                </a>
              );
            })}
          </div>
        ) : (
          <div className="customer-review-empty">
            <AlertTriangle size={21} />
            <span>尚未上传沟通截图</span>
          </div>
        )}
      </section>

      <section className="customer-review-decision">
        <div>
          <span>核对结论</span>
          <strong>资料与截图是否准确？</strong>
        </div>
        <label>
          <span>需补充的说明（退回时必填）</span>
          <textarea
            onChange={(event) => setRemark(event.target.value)}
            placeholder="例如：请补充联系人职务，并上传完整沟通截图"
            rows={3}
            value={remark}
          />
        </label>
        {error ? <p className="customer-review-error">{error}</p> : null}
        {message ? <p className="customer-review-success">{message}</p> : null}
        <div className="customer-review-actions">
          <button
            className="return"
            disabled={reviewing}
            onClick={() => void submitReview("RETURNED")}
            type="button"
          >
            <RotateCcw size={18} />
            退回补充
          </button>
          <button
            disabled={reviewing}
            onClick={() => void submitReview("APPROVED")}
            type="button"
          >
            {reviewing ? (
              <Loader2 className="spin" size={18} />
            ) : (
              <CheckCircle2 size={18} />
            )}
            确认资料准确
          </button>
        </div>
      </section>
    </main>
  );
}
